// src/components/Invoices/InvoiceList.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Button, Box, Typography, Tooltip, Chip, Stack, Divider, Badge,
  Table, TableContainer, TableHead, TableRow, TableCell,
  TableBody, TablePagination, Menu, MenuItem, Checkbox,
  IconButton, LinearProgress,
} from '@mui/material';
import VisibilityIcon       from '@mui/icons-material/Visibility';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import EditIcon             from '@mui/icons-material/Edit';
import OpenInNewIcon        from '@mui/icons-material/OpenInNew';
import EmailIcon            from '@mui/icons-material/Email';
import PaymentsIcon         from '@mui/icons-material/Payments';
import AddIcon              from '@mui/icons-material/Add';
import ViewColumnIcon       from '@mui/icons-material/ViewColumn';
import FormatLineSpacingIcon from '@mui/icons-material/FormatLineSpacing';
import FileDownloadIcon     from '@mui/icons-material/FileDownload';
import RefreshIcon          from '@mui/icons-material/Refresh';
import RestartAltIcon       from '@mui/icons-material/RestartAlt';
import CheckIcon            from '@mui/icons-material/Check';
import TableChartIcon       from '@mui/icons-material/TableChart';
import GridOnIcon           from '@mui/icons-material/GridOn';
import PictureAsPdfIcon     from '@mui/icons-material/PictureAsPdf';
import ReceiptLongIcon      from '@mui/icons-material/ReceiptLong';
import { useHistory, useParams } from 'react-router-dom';
import { UITableShell, UITableToolbar } from '../../../components/ui';
import PaymentSuccessModal  from './PaymentSuccessModal';
import { getInvoicePermissionState, normalizeInvoiceRoles } from '../../../utils/invoicePermissions';
import { filterActiveInvoices, getInactiveInvoiceReason, isInactiveInvoice } from '../../../utils/invoiceVisibility';

 
const INVOICECOLUMNS = [
  { id: 'invoiceId',     label: 'Invoice ID',      minWidth: 160, align: 'left',   visible: true  },
  { id: 'amount',        label: 'Amount',           minWidth: 120, align: 'left',   visible: true  },
  { id: 'customerName',  label: 'Customer',         minWidth: 180, align: 'left',   visible: true  },
  { id: 'paymentStatus', label: 'Payment Status',   minWidth: 150, align: 'left',   visible: true  },
  { id: 'invoiceStatus', label: 'Invoice Status',   minWidth: 160, align: 'left',   visible: true  },
  { id: 'action',        label: 'Actions',          minWidth: 240, align: 'center', visible: true, locked: true },
];

 
const parseInvoiceRelationships = (raw) => {
  if (!raw) return {};
  if (typeof raw === 'string') { try { return JSON.parse(raw); } catch { return {}; } }
  return typeof raw === 'object' ? raw : {};
};

// Visual-only reference. Deposits share the invoice ID sequence and database
// record; do not change the actual ID, accountingRef, or generated-invoice route.
const getInvoiceDisplayId = (invoice) => {
  const rel = parseInvoiceRelationships(invoice?.invoiceRelationships);
  const role = String(rel?.role || rel?.type || '').trim().toLowerCase();
  return `${['deposit', 'deposit-invoice', 'deposit_request', 'depositrequest'].includes(role) ? 'DEP' : 'INV'}-${invoice.id}`;
};

const isPaidInvoice = (invoice) => {
  const payment = String(invoice?.paymentStatus || '').trim().toLowerCase();
  const status  = String(invoice?.invoiceStatus  || '').trim().toLowerCase();
  return (
    ['paid', 'completed', 'succeeded', 'success'].includes(payment) ||
    ['paid', 'completed'].includes(status)
  );
};

const getInvoiceStatusColor = (str) => {
  const s = String(str || '').toLowerCase();
  if (s.includes('paid') || s.includes('completed') || s.includes('succeeded') || s.includes('success'))
    return { bg: 'var(--ec-status-success-bg)', text: 'var(--ec-status-success-text)', border: 'var(--ec-status-success-border)' };
  if (s.includes('sent') || s.includes('active') || s.includes('processing') || s.includes('invoiced'))
    return { bg: 'var(--ec-status-info-bg)',    text: 'var(--ec-status-info-text)',    border: 'var(--ec-status-info-border)'    };
  if (s.includes('pending') || s.includes('draft') || s.includes('awaiting') || s.includes('open'))
    return { bg: 'var(--ec-status-warning-bg)', text: 'var(--ec-status-warning-text)', border: 'var(--ec-status-warning-border)' };
  if (s.includes('cancelled') || s.includes('failed') || s.includes('inactive') || s.includes('void'))
    return { bg: 'var(--ec-status-error-bg)',   text: 'var(--ec-status-error-text)',   border: 'var(--ec-status-error-border)'   };
  return { bg: 'var(--ec-status-neutral-bg)', text: 'var(--ec-status-neutral-text)', border: 'var(--ec-status-neutral-border)' };
};

const formatCurrency = (val) => {
  const n = Number(val);
  if (!Number.isFinite(n)) return '$0.00';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

 
const StatusChip = ({ label }) => {
  if (!label) return <Typography variant="caption" sx={{ color: 'text.disabled' }}>—</Typography>;
  const sc = getInvoiceStatusColor(label);
  return (
    <Box component="span" sx={{
      display: 'inline-flex', alignItems: 'center',
      px: 1, py: 0.25, borderRadius: '999px',
      fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap',
      bgcolor: sc.bg, color: sc.text, border: `1px solid ${sc.border}`,
    }}>
      {label}
    </Box>
  );
};

 
const InvoiceList = ({
  invoices = [],
  invoiceCartSelector = null,
  handleEditClick,
  onCreateInvoice,
  onCreateDeposit,
  createInvoiceActionLabel = 'Create Invoice',
  onResetInvoiced,
  resetInvoicedAccess = false,
  canManageInvoices: canManageInvoicesProp,
  currentUser = null,
  onResendInvoice,
 
  paidTotal = 0,
  invoiceableTotal = 0,
  isCovered = false,
  showProgress = false,
  onRefresh,
}) => {
 
  const [densityMode,      setDensityMode]       = useState('comfortable');
  const [densityAnchor,    setDensityAnchor]     = useState(null);
  const [colMenuAnchor,    setColMenuAnchor]     = useState(null);
  const [exportAnchor,     setExportAnchor]      = useState(null);
  const [page,             setPage]              = useState(0);
  const [rowsPerPage,      setRowsPerPage]       = useState(25);
  const [showInactiveInvoices, setShowInactiveInvoices] = useState(false);
  const [visibleCols,      setVisibleCols]       = useState(
    () => new Set(INVOICECOLUMNS.filter(c => c.visible !== false).map(c => c.id))
  );

  const history = useHistory();
  const { instanceUniqueId, orderId } = useParams();
  const path = `/instance/${instanceUniqueId}/sales/orders/${orderId}`;
  const fallbackPermissions = useMemo(() => getInvoicePermissionState(currentUser), [currentUser]);
  const canManageInvoices = canManageInvoicesProp ?? fallbackPermissions.canManageInvoices;
  const canResetInvoiced = resetInvoicedAccess && normalizeInvoiceRoles(currentUser).has('administrator');
  const visibleColumns = useMemo(
    () => INVOICECOLUMNS.filter((column) => column.id !== 'action' || canManageInvoices),
    [canManageInvoices]
  );
  const safeInvoices = useMemo(
    () => (Array.isArray(invoices) ? invoices : []),
    [invoices]
  );
  const safeActiveInvoices = useMemo(
    () => filterActiveInvoices(safeInvoices),
    [safeInvoices]
  );
  const hasNonDepositInvoice = safeActiveInvoices.some((invoice) => {
    const rel = parseInvoiceRelationships(invoice?.invoiceRelationships);
    const role = String(rel?.role || rel?.type || '').trim().toLowerCase();
    return !['deposit', 'deposit-invoice', 'deposit_request', 'depositrequest'].includes(role)
      && String(rel?.paymentClassification || '').trim().toLowerCase() !== 'full_upfront';
  });
  const inactiveInvoices = useMemo(
    () => safeInvoices.filter((invoice) => isInactiveInvoice(invoice)),
    [safeInvoices]
  );
  const visibleInvoices = showInactiveInvoices ? safeInvoices : safeActiveInvoices;
  const inactiveInvoiceCount = inactiveInvoices.length;

 
  const densitySettings = {
    compact:     { py: 0.75, fontSize: '0.8125rem' },
    comfortable: { py: 1.5,  fontSize: '0.875rem'  },
    spacious:    { py: 2.25, fontSize: '0.9375rem' },
  };
  const density = densitySettings[densityMode];

 
  const toggleCol = (id) => {
    const col = visibleColumns.find(c => c.id === id);
    if (col?.locked) return;
    setVisibleCols(prev => {
      const next = new Set(prev);
      if (next.has(id)) { if (next.size <= 2) return prev; next.delete(id); }
      else next.add(id);
      return next;
    });
  };

 
  const showDepositAction = typeof onCreateDeposit === 'function';
  const handleCreateInvoice = () => {
    if (typeof onCreateInvoice === 'function') { onCreateInvoice(); return; }
    if (typeof handleEditClick === 'function') { handleEditClick(undefined, null); return; }
    history.push(`${path}/invoices/new`);
  };

 
  useEffect(() => {
    setPage(0);
  }, [showInactiveInvoices, inactiveInvoiceCount, visibleInvoices.length]);

  const exportRows = visibleInvoices.map(item => ({
    'Invoice ID':      getInvoiceDisplayId(item),
    'Amount':          formatCurrency(item.amount),
    'Customer':        item.customerName || '',
    'Payment Status':  item.paymentStatus || '',
    'Invoice Status':  item.invoiceStatus || '',
    'Active':          item.isActive ? 'Yes' : 'No',
  }));

  const handleExportCSV = () => {
    if (!exportRows.length) return;
    const headers = Object.keys(exportRows[0]);
    const csv = [
      headers.join(','),
      ...exportRows.map(r => headers.map(h => `"${String(r[h]).replace(/"/g,'""')}"`).join(','))
    ].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    a.download = 'invoices.csv';
    a.click();
  };

  const handleExportExcel = () => {
    if (!exportRows.length) return;
    import('xlsx').then(XLSX => {
      const ws = XLSX.utils.json_to_sheet(exportRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Invoices');
      XLSX.writeFile(wb, 'invoices.xlsx');
    });
  };

  const handleExportPDF = () => {
    if (!exportRows.length) return;
    import('jspdf').then(({ jsPDF }) => {
      import('jspdf-autotable').then(() => {
        const doc = new jsPDF({ orientation: 'landscape' });
        doc.setFontSize(14); doc.text('Invoices', 14, 15);
        doc.setFontSize(9);  doc.text(`Generated ${new Date().toLocaleString()}`, 14, 22);
        doc.autoTable({
          startY: 28,
          head: [Object.keys(exportRows[0])],
          body: exportRows.map(r => Object.values(r)),
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [248, 250, 252] },
        });
        doc.save('invoices.pdf');
      });
    });
  };
 
  const pagedRows = visibleInvoices.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

 
  const progressPct = invoiceableTotal > 0
    ? Math.min(100, Math.round((paidTotal / invoiceableTotal) * 100))
    : 0;
 
  return (
    <>
      <UITableShell
        customLayout
        paperSx={{
          borderRadius: '10px',
          border: '1px solid',
          borderColor: 'divider',
          borderTop: '3px solid',
          borderTopColor: 'primary.main',
          boxShadow: '0 1px 3px 0 rgba(0,0,0,0.07), 0 1px 2px -1px rgba(0,0,0,0.07)',
          display: 'flex',
          flexDirection: 'column',
          mt: 0,
        }}
      >
      
        <UITableToolbar sx={{ backgroundColor: 'background.paper', p: 0 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
            sx={{ px: 2, pt: 1.25, pb: showProgress ? 0.75 : 1.25,
                  borderBottom: showProgress ? 'none' : '1px solid',
                  borderColor: 'divider' }}
          >
            
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight={700} sx={{ color: 'text.primary', lineHeight: 1 }}>
                Invoices
              </Typography>
              {invoiceCartSelector}
              {visibleInvoices.length > 0 && (
                <Chip
                  label={`${visibleInvoices.length} invoice${visibleInvoices.length !== 1 ? 's' : ''}`}
                  size="small"
                  sx={{
                    fontWeight: 700, fontSize: '0.72rem', height: 22,
                    bgcolor: 'var(--ec-status-info-bg)',
                    color: 'var(--ec-status-info-text)',
                    border: '1px solid var(--ec-status-info-border)',
                    '& .MuiChip-label': { px: '8px' },
                  }}
                />
              )}
            </Stack>

            
            <Stack direction="row" alignItems="center" spacing={0.75}>
              
              <Tooltip title="Show/hide columns" arrow>
                <IconButton size="small" onClick={e => setColMenuAnchor(e.currentTarget)} sx={{ color: 'text.secondary' }}>
                  <ViewColumnIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Row density" arrow>
                <IconButton size="small" onClick={e => setDensityAnchor(e.currentTarget)} sx={{ color: 'text.secondary' }}>
                  <FormatLineSpacingIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Tooltip
                title={
                  inactiveInvoiceCount === 0
                    ? 'No inactive invoices'
                    : (showInactiveInvoices ? 'Hide voided and inactive invoices' : 'Show voided and inactive invoices')
                }
                arrow
              >
                <span>
                  <IconButton
                    size="small"
                    disabled={inactiveInvoiceCount === 0}
                    aria-pressed={showInactiveInvoices}
                    onClick={() => setShowInactiveInvoices((prev) => !prev)}
                    sx={{
                      color: showInactiveInvoices ? 'primary.main' : 'text.secondary',
                      bgcolor: showInactiveInvoices ? 'action.selected' : 'transparent',
                      border: '1px solid',
                      borderColor: showInactiveInvoices ? 'primary.main' : 'divider',
                      '&:hover': { bgcolor: showInactiveInvoices ? 'action.selected' : 'action.hover' },
                    }}
                  >
                    <Badge
                      color="warning"
                      badgeContent={inactiveInvoiceCount > 0 ? inactiveInvoiceCount : null}
                      overlap="circular"
                    >
                      <VisibilityOffOutlinedIcon sx={{ fontSize: 18 }} />
                    </Badge>
                  </IconButton>
                </span>
              </Tooltip>
              {canManageInvoices && (
              <Tooltip title="Export" arrow>
                <IconButton size="small" onClick={e => setExportAnchor(e.currentTarget)} sx={{ color: 'text.secondary' }}>
                  <FileDownloadIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              )}
              {typeof onRefresh === 'function' && (
                <Tooltip title="Refresh" arrow>
                  <IconButton size="small" onClick={onRefresh} sx={{ color: 'text.secondary' }}>
                    <RefreshIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}

 <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 24, alignSelf: "center" }} />


              {canResetInvoiced && hasNonDepositInvoice && typeof onResetInvoiced === 'function' && (
                <Tooltip title="Undo the newest eligible non-deposit invoice; deposits are never reset" arrow>
                  <Button
                    variant="outlined"
                    size="small"
                    color="warning"
                    startIcon={<RestartAltIcon sx={{ fontSize: 16 }} />}
                    onClick={onResetInvoiced}
                    sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.8rem', height: 32, borderRadius: '8px', whiteSpace: 'nowrap' }}
                  >
                    Reset Invoiced
                  </Button>
                </Tooltip>
              )}
              {canManageInvoices && (
                <Stack direction="row" spacing={1} alignItems="center">
                {showDepositAction && (
                  <Tooltip title="Use the approved Sales Order deposit terms" arrow>
                    <Button variant="outlined" size="small" onClick={onCreateDeposit}
                      sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.8rem', height: 32, borderRadius: '8px', whiteSpace: 'nowrap' }}>
                      Create Deposit
                    </Button>
                  </Tooltip>
                )}
                <Tooltip title="Create an invoice for this order" arrow>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon sx={{ fontSize: 15 }} />}
                    onClick={handleCreateInvoice}
                    sx={{
                      textTransform: 'none', fontWeight: 700,
                      fontSize: '0.8rem', height: 32, borderRadius: '8px',
                      boxShadow: 'none', whiteSpace: 'nowrap', minWidth: 130,
                    }}
                  >
                    Create Invoice
                  </Button>
                </Tooltip>
                </Stack>
              )}
            </Stack>
          </Stack>
 
          {showProgress && (
            <Box sx={{ px: 2, pb: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <PaymentsIcon sx={{ fontSize: 14, color: isCovered ? 'success.main' : 'warning.main' }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                    {formatCurrency(paidTotal)} paid
                    {invoiceableTotal > 0 && ` of ${formatCurrency(invoiceableTotal)}`}
                  </Typography>
                  {isCovered && (
                    <Chip
                      label="Fully paid"
                      size="small"
                      sx={{
                        height: 18, fontSize: '0.65rem', fontWeight: 700,
                        bgcolor: 'var(--ec-status-success-bg)',
                        color: 'var(--ec-status-success-text)',
                        border: '1px solid var(--ec-status-success-border)',
                        '& .MuiChip-label': { px: '6px' },
                      }}
                    />
                  )}
                </Stack>
                {invoiceableTotal > 0 && (
                  <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 700 }}>
                    {progressPct}%
                  </Typography>
                )}
              </Stack>
              {invoiceableTotal > 0 && (
                <LinearProgress
                  variant="determinate"
                  value={progressPct}
                  sx={{
                    height: 5, borderRadius: 99,
                    bgcolor: 'action.hover',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: isCovered ? 'success.main' : 'warning.main',
                      borderRadius: 99,
                    },
                  }}
                />
              )}
            </Box>
          )}
        </UITableToolbar>

 
        <TableContainer
          sx={{
            flex: 1, overflow: 'auto', minHeight: 280, maxHeight: 520,
            '&::-webkit-scrollbar': { width: 6, height: 6 },
            '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(148,163,184,0.8)', borderRadius: 99 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
          }}
        >
          <Table stickyHeader size="small" sx={{ minWidth: 860 }}>
            <TableHead>
              <TableRow>
                {visibleColumns.filter(c => visibleCols.has(c.id)).map(col => (
                  <TableCell
                    key={col.id}
                    align={col.align || 'left'}
                    sx={{
                      minWidth: col.minWidth,
                      px: 2, py: 1.25,
                      backgroundColor: '#1e293b !important',
                      color: '#f8fafc !important',
                      fontWeight: 700,
                      fontSize: '0.72rem',
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      borderBottom: 'none',
                      whiteSpace: 'nowrap',
                      position: 'sticky', top: 0, zIndex: 2,
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody
              sx={{
                '& .MuiTableCell-root': { borderBottom: '1px solid', borderColor: 'divider' },
                '& .MuiTableRow-root:last-child .MuiTableCell-root': { borderBottom: 'none' },
              }}
            >
              {pagedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length} sx={{ border: 'none', p: 0 }}>
                    <Box sx={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center',
                      justifyContent: 'center', py: 8, color: 'text.disabled',
                    }}>
                      <ReceiptLongIcon sx={{ fontSize: 48, mb: 1.5, opacity: 0.25 }} />
                      <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5, color: 'text.secondary' }}>
                        {inactiveInvoiceCount > 0 && !showInactiveInvoices ? 'No active invoices or deposits' : 'No invoices or deposits yet'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.disabled', mb: canManageInvoices ? 2 : 0 }}>
                        {inactiveInvoiceCount > 0 && !showInactiveInvoices
                          ? 'Previous invoices or deposits that were voided or made inactive are hidden. Use the visibility toggle above to view them.'
                          : (canManageInvoices ? 'Create a deposit or invoice to begin billing this sales order.' : 'No invoices or deposits are available to view yet.')}
                      </Typography>
                      {canManageInvoices && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          {showDepositAction && (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<AddIcon sx={{ fontSize: 15 }} />}
                              onClick={onCreateDeposit}
                              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px' }}
                            >
                              Create Deposit
                            </Button>
                          )}
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<AddIcon sx={{ fontSize: 15 }} />}
                            onClick={handleCreateInvoice}
                            sx={{
                              textTransform: 'none', fontWeight: 700,
                              fontSize: '0.8rem', height: 32, borderRadius: '8px',
                              boxShadow: 'none', whiteSpace: 'nowrap', minWidth: 130,
                            }}
                          >
                            Create Invoice
                          </Button>
                        </Stack>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                pagedRows.map((item, idx) => {
                  const rel             = parseInvoiceRelationships(item?.invoiceRelationships);
                  const role            = String(rel?.role || rel?.type || '').toLowerCase();
                  const isDeposit       = ['deposit', 'deposit-invoice', 'deposit_request', 'depositrequest'].includes(role);
                  const linkedSoId      = rel?.salesOrderId || null;
                  const isPaid          = isPaidInvoice(item);
                  const inactive        = isInactiveInvoice(item);
                  const inactiveReason  = getInactiveInvoiceReason(item);
                  const completed       = String(item?.invoiceStatus || '').toLowerCase() === 'completed';

                  return (
                    <TableRow
                      key={item.id || idx}
                      hover
                      sx={{
                        cursor: 'default',
                        bgcolor: inactive
                          ? 'action.hover'
                          : isPaid
                          ? 'var(--ec-status-success-bg)'
                          : idx % 2 === 0 ? 'background.paper' : '#f8fafc',
                        transition: 'background-color 0.12s',
                        opacity: inactive ? 0.78 : 1,
                      }}
                    >
               
                      {visibleCols.has('invoiceId') && (
                        <TableCell sx={{ px: 2, py: density.py }}>
                          <Stack direction="row" alignItems="center" spacing={0.75}>
                            <Typography variant="caption" sx={{
                              fontFamily: 'monospace', fontWeight: 700,
                              fontSize: density.fontSize, color: 'text.primary',
                            }}>
                              {getInvoiceDisplayId(item)}
                            </Typography>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => window.open(
                                `${process.env.REACT_APP_NODEJS_CLI_URL}/generated-invoice/${item.id}`,
                                '_blank',
                                'noopener,noreferrer'
                              )}
                              sx={{
                                minWidth: 0,
                                px: 0.5,
                                textTransform: 'none',
                                fontSize: '0.7rem',
                                fontWeight: 700,
                              }}
                            >
                              Open invoice
                            </Button>
                            <Chip
                              size="small"
                              label={isDeposit ? 'Deposit' : 'Invoice'}
                              sx={{
                                height: 18, fontSize: '0.65rem', fontWeight: 700,
                                ...(isDeposit
                                  ? { bgcolor: 'var(--ec-status-info-bg)', color: 'var(--ec-status-info-text)', border: '1px solid var(--ec-status-info-border)' }
                                  : { bgcolor: 'var(--ec-status-neutral-bg)', color: 'var(--ec-status-neutral-text)', border: '1px solid var(--ec-status-neutral-border)' }
                                ),
                                '& .MuiChip-label': { px: '6px' },
                              }}
                            />
                          </Stack>
                        </TableCell>
                      )}

 
                      {visibleCols.has('amount') && (
                        <TableCell sx={{ px: 2, py: density.py }}>
                          <Typography variant="body2" sx={{
                            fontWeight: 700, fontSize: density.fontSize,
                            color: isPaid ? 'success.main' : 'text.primary',
                          }}>
                            {formatCurrency(item.amount)}
                          </Typography>
                        </TableCell>
                      )}
 
                      {visibleCols.has('customerName') && (
                        <TableCell sx={{ px: 2, py: density.py }}>
                          {item.customerName
                            ? <Typography variant="body2" sx={{ fontSize: density.fontSize }}>{item.customerName}</Typography>
                            : <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>Awaiting payment</Typography>
                          }
                        </TableCell>
                      )}

 
                      {false && (
                        <TableCell sx={{ px: 2, py: density.py }}>
                          {item.paymentMethod
                            ? <Typography variant="body2" sx={{ fontSize: density.fontSize }}>{item.paymentMethod}</Typography>
                            : <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>—</Typography>
                          }
                        </TableCell>
                      )}

  
                      {visibleCols.has('paymentStatus') && (
                        <TableCell sx={{ px: 2, py: density.py }}>
                          <StatusChip label={item.paymentStatus} />
                        </TableCell>
                      )}

  
                      {visibleCols.has('invoiceStatus') && (
                        <TableCell sx={{ px: 2, py: density.py }}>
                          <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap">
                            <StatusChip label={item.invoiceStatus} />
                            {inactive && (
                              <Chip
                                size="small"
                                label={inactiveReason ? `${inactiveReason.charAt(0).toUpperCase()}${inactiveReason.slice(1)}` : 'Inactive'}
                                sx={{
                                  height: 18, fontSize: '0.65rem', fontWeight: 700,
                                  bgcolor: 'var(--ec-status-error-bg)',
                                  color: 'var(--ec-status-error-text)',
                                  border: '1px solid var(--ec-status-error-border)',
                                  '& .MuiChip-label': { px: '6px' },
                                }}
                              />
                            )}
                          </Stack>
                        </TableCell>
                      )}
 
                      {canManageInvoices && visibleCols.has('action') && (
                        <TableCell align="center" sx={{ px: 2, py: density.py }}>
                            <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                        
                              {!completed && (
                                <Tooltip title={inactive ? 'Invoice is inactive' : 'Edit invoice'} arrow>
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={inactive}
                                      onClick={() => {
                                        if (!inactive && typeof handleEditClick === 'function')
                                          handleEditClick(item.id, item);
                                      }}
                                      sx={{
                                        color: 'primary.main', width: 28, height: 28,
                                        '&:hover': { bgcolor: 'primary.main', color: '#fff' },
                                        '&.Mui-disabled': { opacity: 0.35 },
                                      }}
                                    >
                                      <EditIcon sx={{ fontSize: 15 }} />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}

                             
                              <Tooltip title="View invoice" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => window.open(
                                    `${process.env.REACT_APP_NODEJS_CLI_URL}/generated-invoice/${item.id}`,
                                    '_blank', 'noopener,noreferrer'
                                  )}
                                  sx={{
                                    color: 'text.secondary', width: 28, height: 28,
                                    '&:hover': { bgcolor: 'action.hover' },
                                  }}
                                >
                                  <VisibilityIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Tooltip>

                        
                              {linkedSoId && (
                                <Tooltip title="Open linked sales order" arrow>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
                                    onClick={() => history.push(`${path}/sales-order/${linkedSoId}`)}
                                    sx={{
                                      textTransform: 'none', fontWeight: 600,
                                      fontSize: '0.72rem', height: 28, borderRadius: '8px',
                                      px: 1,
                                    }}
                                  >
                                    SO
                                  </Button>
                                </Tooltip>
                              )}

                          
                              <Tooltip title="Resend invoice email" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => typeof onResendInvoice === 'function' && onResendInvoice(item)}
                                  sx={{
                                    color: 'text.secondary', width: 28, height: 28,
                                    '&:hover': { bgcolor: 'action.hover' },
                                  }}
                                >
                                  <EmailIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

       
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2, py: 0.5, borderTop: '1px solid', borderColor: 'divider',
          bgcolor: 'background.paper', flexShrink: 0,
        }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {visibleInvoices.length > 0
              ? `Showing ${page * rowsPerPage + 1}–${Math.min(visibleInvoices.length, page * rowsPerPage + rowsPerPage)} of ${visibleInvoices.length} invoice${visibleInvoices.length !== 1 ? 's' : ''}`
              : 'No invoices'
            }
          </Typography>
          <TablePagination
            component="div"
            count={visibleInvoices.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
            sx={{
              border: 0,
              '& .MuiTablePagination-toolbar': { minHeight: 40, px: 0, alignItems: 'center' },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontSize: '0.78rem', color: 'text.secondary', mb: 0, lineHeight: 1,
              },
              '& .MuiTablePagination-select': { fontSize: '0.78rem' },
            }}
          />
        </Box>
      </UITableShell>

       
      <Menu
        anchorEl={colMenuAnchor} open={Boolean(colMenuAnchor)}
        onClose={() => setColMenuAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 210, borderRadius: 2 } } }}
      >
        <Typography variant="overline" sx={{ px: 2, pt: 1.25, pb: 0.5, display: 'block', color: 'text.disabled' }}>
          Columns
        </Typography>
        <Divider sx={{ mb: 0.5 }} />
        {visibleColumns.filter(c => !c.locked).map(col => (
          <MenuItem key={col.id} onClick={() => toggleCol(col.id)} sx={{ py: 0.4 }}>
            <Checkbox size="small" checked={visibleCols.has(col.id)} sx={{ mr: 1, p: 0.25 }} />
            {col.label}
          </MenuItem>
        ))}
      </Menu>

    
      <Menu
        anchorEl={densityAnchor} open={Boolean(densityAnchor)}
        onClose={() => setDensityAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 160, borderRadius: 2 } } }}
      >
        <Typography variant="overline" sx={{ px: 2, pt: 1.25, pb: 0.5, display: 'block', color: 'text.disabled' }}>
          Row Density
        </Typography>
        <Divider sx={{ mb: 0.5 }} />
        {[
          { key: 'compact',     label: 'Compact'     },
          { key: 'comfortable', label: 'Comfortable' },
          { key: 'spacious',    label: 'Spacious'    },
        ].map(opt => (
          <MenuItem
            key={opt.key}
            selected={densityMode === opt.key}
            onClick={() => { setDensityMode(opt.key); setDensityAnchor(null); }}
            sx={{ py: 0.75, fontSize: '0.875rem' }}
          >
            {densityMode === opt.key
              ? <CheckIcon sx={{ fontSize: 15, mr: 1, color: 'primary.main' }} />
              : <Box sx={{ width: 15, mr: 1 }} />
            }
            {opt.label}
          </MenuItem>
        ))}
      </Menu>

      
      {canManageInvoices && (
      <Menu
        anchorEl={exportAnchor} open={Boolean(exportAnchor)}
        onClose={() => setExportAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 180, borderRadius: 2 } } }}
      >
        <Typography variant="overline" sx={{ px: 2, pt: 1.25, pb: 0.5, display: 'block', color: 'text.disabled' }}>
          Export As
        </Typography>
        <Divider sx={{ mb: 0.5 }} />
        <MenuItem onClick={() => { handleExportCSV(); setExportAnchor(null); }} sx={{ py: 0.75, fontSize: '0.875rem', gap: 1 }}>
          <TableChartIcon sx={{ fontSize: 16, color: 'text.secondary' }} /> CSV
        </MenuItem>
        <MenuItem onClick={() => { handleExportExcel(); setExportAnchor(null); }} sx={{ py: 0.75, fontSize: '0.875rem', gap: 1 }}>
          <GridOnIcon sx={{ fontSize: 16, color: 'success.main' }} /> Excel
        </MenuItem>
        <MenuItem onClick={() => { handleExportPDF(); setExportAnchor(null); }} sx={{ py: 0.75, fontSize: '0.875rem', gap: 1 }}>
          <PictureAsPdfIcon sx={{ fontSize: 16, color: 'error.main' }} /> PDF
        </MenuItem>
      </Menu>
      )}

      <PaymentSuccessModal />
    </>
  );
};

export default InvoiceList;
